// stores/layout.ts
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type { PersistenceOptions } from 'pinia-plugin-persistedstate';

export interface LayoutInputs {
  page: number;
  itemsPerPage: number;
  sortBy: string;
  direction: 'asc' | 'desc';
  currentView: string;
  filters: Record<string, any>;
  filters_records: Record<string, any>;
}

export interface LayoutMeta {
  totalItems: number;
  dataLoading: boolean;
}

export type LayoutConfig = LayoutInputs & LayoutMeta;
type LayoutsRecord = Record<string, LayoutConfig>;

export const useLayoutStore = defineStore(
  'layout',
  () => {
    const layouts = ref({}) as Ref<LayoutsRecord>;

    const initLayout = (moduleName: string, defaultConfig?: Partial<LayoutConfig>) => {
      if (!layouts.value[moduleName]) {
        layouts.value[moduleName] = {
          page: 1,
          itemsPerPage: 10,
          totalItems: 0,
          sortBy: 'id',
          direction: 'desc',
          currentView: moduleName=='Opportunities'?'kanban' : (moduleName=='Activities'? 'calendar' : 'table'),
          filters: {},
          filters_records: {},
          dataLoading: false,
          ...defaultConfig,
        };
      }
    };

    const updateLayout = (moduleName: string, updatedConfig: Partial<LayoutConfig>) => {
      if (!layouts.value[moduleName]) return;
      const current = layouts.value[moduleName];
      const newConfig = { ...current, ...updatedConfig };

      const isDifferent = Object.keys(updatedConfig).some(
        (key) => updatedConfig[key as keyof LayoutConfig] !== current[key as keyof LayoutConfig]
      );

      if (isDifferent) {
        layouts.value[moduleName] = newConfig;
      }
    };

    const getLayout = (moduleName: string): LayoutConfig => {
      return layouts.value[moduleName] || null;
    };

    const $reset = () => {
      layouts.value = {};
    };

    return {
      layouts,
      initLayout,
      updateLayout,
      getLayout,
      $reset
    };
  },
  {
    // ⛑️ cast explícito para evitar errores de tipado
    persist: {
      storage: localStorage,
      paths: ['layouts'],
    } as PersistenceOptions,
  }
);
